import { z } from 'zod';

import { jobProfileSkillSchema } from './skill';
import { baseUserSchema } from './user';
import { languageCodeSchemaForJSA } from '../internationalization';

export const competencyTranslationSchema = z.object({
    language: z.string().max(5).min(5),
    title: z.string(),
});

export const competencySchema = z.object({
    id: z.uuid(),
    title: z.string(),
    titles: competencyTranslationSchema.array(),
});

export type Competency = z.infer<typeof competencySchema>;

export const jobProfileVersionStructuredDescriptionSchema = z.object({
    tag: z.string(),
    value: z.string(),
});

export const jobProfileVersionCompetencyTranslationSchema = z.object({
    language: z.string().max(5).min(5),
    title: z.string(),
});

export const jobProfileVersionReviewerStateSchema = z.union([z.literal('assigned'), z.literal('approved')]);

export type JobProfileVersionReviewerState = z.infer<typeof jobProfileVersionReviewerStateSchema>;

export const jobProfileVersionReviewerSchema = baseUserSchema.extend({
    emailAddress: z.string().optional(),
    state: jobProfileVersionReviewerStateSchema,
});

export type JobProfileVersionReviewer = z.infer<typeof jobProfileVersionReviewerSchema>;

export const jobProfileStructuredDescriptionSchema = z.object({
    tag: z.string().regex(/^[a-z]+[a-z_]*$/, 'Tag must be lowercase and can only contain letters and underscores'),
    value: z.string().max(100_000),
});

export const jobProfileTranslationsSchema = z.object({
    description: z.string().max(100_000).optional(),
    language: languageCodeSchemaForJSA,
    structured: jobProfileStructuredDescriptionSchema.array().optional(),
    title: z.string().max(256).min(3),
});

export type JobProfileVersionTranslation = z.infer<typeof jobProfileTranslationsSchema>;

export const competenciesSchema = competencySchema.array().transform((competencies) => {
    return competencies.reduce((competencyMap, item) => {
        competencyMap.set(item.id, item);

        return competencyMap;
    }, new Map<string, Competency>());
});

export const jobProfileVersionSchema = z.object({
    architectureId: z.uuid(),
    competencies: competenciesSchema.optional(),
    createdAt: z.iso.datetime(),
    createdBy: z.nullable(baseUserSchema),
    itemId: z.uuid(),
    layer: z.union([z.literal('root'), z.literal('role'), z.literal('level')]),
    publishedAt: z.nullable(z.iso.datetime()),
    publishedBy: z.nullable(baseUserSchema),
    reviewedAt: z.nullable(z.iso.datetime()),
    reviewer: z.nullable(jobProfileVersionReviewerSchema).optional(),
    skills: jobProfileSkillSchema.array(),
    translations: jobProfileTranslationsSchema.array(),
    updatedAt: z.iso.datetime(),
    updatedBy: z.nullable(baseUserSchema),
    version: z.number(),
});

export type JobProfileVersion = z.infer<typeof jobProfileVersionSchema>;

export const jobProfileVersionsSchema = jobProfileVersionSchema.array();

export type JobProfileVersions = z.infer<typeof jobProfileVersionsSchema>;

export const versionInformationSchema = z.object({
    createdAt: z.iso.datetime(),
    isPublished: z.boolean(),
    isReviewed: z.boolean(),
    publishedAt: z.nullable(z.iso.datetime()).optional(),
    publishedBy: z.nullable(baseUserSchema).optional(),
    reviewedAt: z.iso.datetime().optional(),
    translationOriginLanguage: languageCodeSchemaForJSA.optional(),
    version: z.number(),
});

export type VersionInformation = z.infer<typeof versionInformationSchema>;
